// Sentinelas de `familyKey` — FONTE ÚNICA.
//
// O agrupamento client-side emite dois tipos de chave de grupo:
//   1. Chave de família REAL — ou a curada (`live_family_override.family_key`),
//      ou a heurística de nome (`cat::globo`). Tem contrapartida no domínio.
//   2. Chave SINTÉTICA — "este canal não tem família": ele é isolado por
//      curadoria, ou não tem nome utilizável. Existe só pra dar identidade
//      própria ao canal dentro da UI. NUNCA corresponde a uma linha de
//      `live_family_override`.
//
// Por que este módulo existe (e por que o predicado não é inline nas rotas):
// as sentinelas eram enumeradas à mão em 2 rotas de `apps/api`, longe do
// código que as CRIA. Quando o agrupamento passou a emitir `__unnamed__::`
// (canal sem nome vira grupo próprio em vez de colapsar todo mundo numa chave
// vazia), as duas rotas não souberam: um `DELETE .../by-key/__unnamed__::<id>`
// passava o guard de sentinela, ia ao banco, não casava nada e respondia
// `{ok:true, removed:0}` — um "desfiz" que mentia. Antes disso a chave era `''`
// e reprovava no `z.string().min(1)`, ou seja, o comportamento correto vinha
// por ACIDENTE de validação, não por design.
//
// Regra: quem cria sentinela cria AQUI (a constante de prefixo) e usa a
// constante ao montar a chave. `isSyntheticFamilyKey` deriva do mesmo array,
// então registrar o prefixo já registra o predicado nos dois consumidores.
// `family-keys.test.ts` varre o fonte do pacote atrás de literais no formato
// `__NOME__::` e falha se algum prefixo escapar deste registro. (O placeholder
// aqui vai em CAIXA ALTA de propósito: o gate casa `__[a-z]+__::`, então um
// exemplo em minúsculas dentro de um comentário seria pego como sentinela de
// verdade — foi o que aconteceu na primeira rodada.)
/**
 * Valor GRAVADO NO BANCO em `live_family_override.family_key` quando o curador
 * marca um canal como isolado. Não é prefixo: é a string inteira.
 */
export const FAMILY_KEY_ISOLATED = 'isolated';
/** Prefixo da chave sintética de canal isolado por curadoria. */
export const ISOLATED_KEY_PREFIX = '__isolated__::';
/**
 * Prefixo da chave sintética de canal SEM NOME utilizável (nome cru null,
 * ausente, não-string ou só-espaços — ver `parsableNameOf`). Cada canal vira
 * grupo próprio: sem nome não há evidência NENHUMA de identidade compartilhada,
 * e colapsar todos num grupo só sumia com canal da grade.
 */
export const UNNAMED_KEY_PREFIX = '__unnamed__::';
/**
 * Prefixo da chave de família CURADA. Não é sentinela — embrulha um
 * `familyKey` real que existe no banco. Fica aqui só pra o gate de registro
 * enxergar o literal e pra ninguém confundir os dois grupos de prefixo.
 */
export const CURATED_KEY_PREFIX = '__family__::';
/**
 * Todo prefixo que denota "canal SEM família real". Ordem irrelevante.
 * NÃO inclui `CURATED_KEY_PREFIX` — aquele embrulha uma família de verdade.
 */
export const SYNTHETIC_FAMILY_KEY_PREFIXES = [
    ISOLATED_KEY_PREFIX,
    UNNAMED_KEY_PREFIX,
];
/**
 * True quando `key` não designa uma família real e portanto não pode ser
 * criada, apagada nem procurada em `live_family_override`.
 *
 * Cobre os dois formatos: o valor de banco (`'isolated'`) e os prefixos
 * sintéticos do agrupamento client-side, que chegam de volta ao servidor
 * porque saem no payload de `GET /families`.
 *
 * Tolerante a entrada não-string de propósito — é chamado com dado vindo da
 * request, e um `undefined` aqui não pode virar TypeError dentro do handler.
 */
export function isSyntheticFamilyKey(key) {
    if (typeof key !== 'string')
        return false;
    if (key === FAMILY_KEY_ISOLATED)
        return true;
    for (const prefix of SYNTHETIC_FAMILY_KEY_PREFIXES) {
        // `slice` e não `startsWith`: este módulo é compartilhado com o bundle de
        // TV, cujo piso é Chr38 — `String.prototype.startsWith` só entrou no Chr41.
        // Hoje o plugin-legacy poliprenche, mas depender de polyfill num predicado
        // de segurança/contrato é aposta desnecessária. `slice` é ES3.
        if (key.slice(0, prefix.length) === prefix)
            return true;
    }
    return false;
}
//# sourceMappingURL=family-keys.js.map